import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"
import { confirmNewCompany } from "../lib/company_duplicate"

export default class extends Controller {
  static targets = [
    "searchInput", "frame",
    // Create modal
    "createModal", "createName", "createAddress", "createCountry", "createState",
    "createCity", "createZipcode", "createLanguage",
    "createContactName", "createContactEmail", "createContactPhone",
    "createSubmitBtn",
    // Edit modal
    "editModal", "editId", "editName", "editCreatedAt", "editAddress",
    "editCountry", "editState", "editCity", "editZipcode", "editSubmitBtn",
    "editContactName", "editContactEmail", "editContactPhone", "editContactUserSelect",
    // View popup (row click)
    "viewModal", "viewName", "viewMeta", "viewContact", "viewAutoHint", "viewDocuments",
    "viewPhone", "viewEmail", "viewAddress",
    // Export
    "exportButton",
    // Bulk document chase (Larry 2026-08-06)
    "bulkRequestModal", "bulkRequestConfirm",
    "requestModal", "requestDocLabel", "requestRecipients", "requestComment",
    "requestError", "requestSend",
    // Remove + trash
    "removeModal", "removeName", "removeConfirm",
    "removedModal", "removedEmpty", "removedLoading"
  ]

  static values = {
    listVendorsUrl: String,
    createUrl: String,
    updateUrl: String,
    vendorUsersUrl: String,
    exportUrl: String,
    removeUrl: String,
    removedListUrl: String,
    restoreUrl: String,
    purgeUrl: String,
    label: { type: String, default: "vendor" }
  }

  connect() {
    this.currentSort = "name"
    this.currentOrder = "asc"
    this.searchTimeout = null
  }

  // ── Search ────────────────────────────────────────────────────────

  search() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this._reloadFrame(), 300)
  }

  // ── Sort ──────────────────────────────────────────────────────────

  sortByColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this.currentSort === key) {
      this.currentOrder = this.currentOrder === "asc" ? "desc" : "asc"
    } else {
      this.currentSort = key
      this.currentOrder = "asc"
    }
    this._reloadFrame()
  }

  // ── Create Modal ──────────────────────────────────────────────────

  openCreateModal() {
    this._resetCreateForm()
    this.createModalTarget.classList.remove("hidden")
  }

  closeCreateModal() {
    this.createModalTarget.classList.add("hidden")
    this._resetCreateForm()
  }

  async submitCreateVendor() {
    const name = this.createNameTarget.value.trim()
    if (!name) {
      this.createNameTarget.focus()
      return
    }

    // "Already in ConsLog?" (Kloepfer K8-6, Phelipe 2026-10-27): the same AI
    // check as everywhere else a company is registered. Using the existing
    // company lists it as your vendor instead of making a twin.
    const btn = this.createSubmitBtnTarget
    btn.disabled = true
    const { choice, company } = await confirmNewCompany(name, { screen: "vendor_management" })
    btn.disabled = false
    if (choice === "cancel") return

    this._doCreate(choice === "existing" ? company.id : null)
  }

  createCountryChanged() {
    this._loadCreateSubregions(this.createCountryTarget.value)
  }

  async _doCreate(existingCompanyId = null) {
    const btn = this.createSubmitBtnTarget
    btn.disabled = true
    btn.textContent = "Creating..."

    const body = {
      vendor: {
        name: this.createNameTarget.value.trim(),
        address: this.createAddressTarget.value.trim(),
        country_code: this.createCountryTarget.value.trim(),
        sub_country_code: this.createStateTarget.value.trim(),
        city: this.createCityTarget.value.trim(),
        zipcode: this.createZipcodeTarget.value.trim(),
        default_language: this.createLanguageTarget.value,
        primary_contact_name: this.createContactNameTarget.value.trim(),
        primary_contact_email: this.createContactEmailTarget.value.trim(),
        primary_contact_phone: this.createContactPhoneTarget.value.trim()
      }
    }
    if (existingCompanyId) body.existing_company_id = existingCompanyId

    try {
      const resp = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify(body)
      })

      if (resp.ok) {
        const data = await resp.json()
        this.closeCreateModal()
        this._reloadFrame()
        this._showToast(data.existing ? "Vendor added" : "Vendor created successfully")
        track(data.existing ? "Vendor Added From Duplicate" : "Vendor Created", { vendor_id: data.id })
      } else {
        const data = await resp.json()
        this._showToast(data.errors ? data.errors.join(", ") : "Error creating vendor", "error")
      }
    } catch (e) {
      this._showToast("Error creating vendor", "error")
    } finally {
      btn.disabled = false
      btn.textContent = "Create"
    }
  }

  _resetCreateForm() {
    if (this.hasCreateNameTarget) this.createNameTarget.value = ""
    if (this.hasCreateAddressTarget) this.createAddressTarget.value = ""
    if (this.hasCreateCountryTarget) this.createCountryTarget.value = ""
    if (this.hasCreateStateTarget) {
      this.createStateTarget.value = ""
      this.createStateTarget.innerHTML = '<option value=""></option>'
    }
    if (this.hasCreateCityTarget) this.createCityTarget.value = ""
    if (this.hasCreateZipcodeTarget) this.createZipcodeTarget.value = ""
    if (this.hasCreateLanguageTarget) this.createLanguageTarget.value = ""
    if (this.hasCreateContactNameTarget) this.createContactNameTarget.value = ""
    if (this.hasCreateContactEmailTarget) this.createContactEmailTarget.value = ""
    if (this.hasCreateContactPhoneTarget) this.createContactPhoneTarget.value = ""
  }

  // ── View Popup (row click) ────────────────────────────────────────

  openViewModal(event) {
    // Ignore clicks that came from the row's own controls (kebab menu, links).
    if (event.target.closest("button, a")) return

    const ds = event.currentTarget.dataset
    this._viewDataset = { ...ds }

    const dash = "—"
    this.viewNameTarget.textContent = ds.vendorName || dash
    const projectsLabel = this.viewModalTarget.dataset.projectsLabel || "Projects"
    const meta = [`${ds.vendorProjectsCount || 0} ${projectsLabel}`, ds.vendorStateDisplay]
      .filter(Boolean).join(" · ")
    this.viewMetaTarget.textContent = meta
    this.viewContactTarget.textContent = ds.vendorViewContactName || dash
    this.viewPhoneTarget.textContent = ds.vendorViewContactPhone || dash
    this.viewEmailTarget.textContent = ds.vendorViewContactEmail || dash
    this.viewAddressTarget.textContent = ds.vendorFullAddress || dash
    const auto = ds.vendorContactAuto === "1" && !!ds.vendorViewContactName
    this.viewAutoHintTarget.classList.toggle("hidden", !auto)
    this.viewAutoHintTarget.classList.toggle("block", auto)

    this.viewModalTarget.classList.remove("hidden")
    this._loadDocuments(ds.vendorId)
    track("Vendor Details Viewed", { vendor_id: ds.vendorId, contact_auto_filled: auto })
  }

  // ── Compliance documents (Larry 2026-08-06) ─────────────────────────────
  // Fetched when the modal opens rather than rendered into every row.
  async _loadDocuments(vendorId) {
    if (!this.hasViewDocumentsTarget || !vendorId) return

    const panel = this.viewDocumentsTarget
    panel.innerHTML = '<p class="text-[13px] text-gray-400">Loading documents…</p>'
    try {
      const url = new URL(panel.dataset.documentsUrl, window.location.origin)
      url.searchParams.set("id", vendorId)
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error("not ok")
      panel.innerHTML = await response.text()
    } catch (_error) {
      panel.innerHTML = '<p class="text-[13px] text-gray-400">Documents couldn\'t be loaded.</p>'
    }
  }

  // Chase a sub for one document straight from the directory — no contract
  // needed. Opens the request popup first (Phelipe 2026-08-17): pick who at
  // the company gets the email, add an optional note on top of the template.
  async requestDocument(event) {
    event.stopPropagation()
    const button = event.currentTarget
    this._requestCtx = {
      button,
      vendorId: button.dataset.vendorId,
      docType: button.dataset.docType,
      docLabel: button.dataset.docLabel || "document"
    }
    this.requestDocLabelTarget.textContent = `Request ${this._requestCtx.docLabel}`
    this.requestCommentTarget.value = ""
    this.requestErrorTarget.classList.add("hidden")
    this.requestSendTarget.disabled = false
    this.requestSendTarget.textContent = "Send request"
    this.requestRecipientsTarget.innerHTML = '<p class="text-[13px] text-gray-400">Loading people…</p>'
    this.requestModalTarget.classList.remove("hidden")

    try {
      const url = `${this.vendorUsersUrlValue}?id=${encodeURIComponent(this._requestCtx.vendorId)}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("not ok")
      this._renderRequestRecipients(await response.json())
    } catch (_error) {
      this.requestRecipientsTarget.innerHTML =
        '<p class="text-[13px] text-red-600">People couldn\'t be loaded — close this and try again.</p>'
    }
  }

  closeRequestModal() {
    this.requestModalTarget.classList.add("hidden")
  }

  // The saved directory contact leads (pre-checked), then the company's
  // users, deduped by email.
  _renderRequestRecipients(data) {
    const rows = []
    const seen = new Set()
    const contact = data.contact && data.contact.email ? data.contact : null
    if (contact) {
      seen.add(contact.email.toLowerCase())
      rows.push({ name: contact.name || contact.email, email: contact.email, primary: true })
    }
    ;(data.users || []).forEach((u) => {
      if (!u.email || seen.has(u.email.toLowerCase())) return
      seen.add(u.email.toLowerCase())
      rows.push({ name: u.name || u.email, email: u.email, primary: false })
    })

    if (!rows.length) {
      this.requestRecipientsTarget.innerHTML =
        '<p class="text-[13px] text-gray-500">No people on file for this company yet — add a contact or invite a user first.</p>'
      this.requestSendTarget.disabled = true
      return
    }
    this.requestRecipientsTarget.innerHTML = rows.map((r, i) => `
      <label class="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
        <input type="checkbox" data-request-recipient value="${this._escapeHtml(r.email)}" ${i === 0 ? "checked" : ""}
               class="shrink-0 rounded border-gray-300 text-brand focus:ring-brand">
        <span class="min-w-0">
          <span class="block text-[13px] text-gray-900 truncate">${this._escapeHtml(r.name)}${r.primary ? ' <span class="text-gray-400">· primary contact</span>' : ""}</span>
          <span class="block text-[13px] text-gray-500 truncate">${this._escapeHtml(r.email)}</span>
        </span>
      </label>`).join("")
  }

  async submitRequest() {
    const ctx = this._requestCtx
    const panel = this.hasViewDocumentsTarget ? this.viewDocumentsTarget : null
    if (!ctx || !panel) return

    const emails = Array.from(this.requestRecipientsTarget.querySelectorAll("[data-request-recipient]:checked"))
      .map((box) => box.value)
    if (!emails.length) {
      this.requestErrorTarget.textContent = "Pick at least one person."
      this.requestErrorTarget.classList.remove("hidden")
      return
    }

    this.requestErrorTarget.classList.add("hidden")
    this.requestSendTarget.disabled = true
    this.requestSendTarget.textContent = "Sending…"
    try {
      const response = await fetch(panel.dataset.requestUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({ id: ctx.vendorId, doc_types: [ctx.docType],
                               emails, comment: this.requestCommentTarget.value.trim() })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.ok) throw new Error(data.error || "Couldn't send that request.")
      ctx.button.textContent = "Requested"
      this.closeRequestModal()
      this._showToast(`Request sent to ${emails.length} ${emails.length === 1 ? "person" : "people"}`)
    } catch (error) {
      this.requestErrorTarget.textContent = error.message
      this.requestErrorTarget.classList.remove("hidden")
      this.requestSendTarget.disabled = false
      this.requestSendTarget.textContent = "Send request"
    }
  }

  // File a document the sub sent straight to the builder (Larry 2026-08-06).
  // Presign → PUT to S3 → register, then reload the panel so the row shows
  // the AI's verdict and any expiry it pulled off the file.
  async uploadDocument(event) {
    event.stopPropagation()
    const input = event.currentTarget
    const file = input.files && input.files[0]
    if (!file) return

    const panel = this.hasViewDocumentsTarget ? this.viewDocumentsTarget : null
    if (!panel) return

    const label = input.closest("label")
    const text = label?.querySelector("span")
    const original = text?.textContent
    const vendorId = input.dataset.vendorId
    input.value = ""
    if (text) text.textContent = "Checking…"

    try {
      const presign = await this._postJson(panel.dataset.presignUrl, {
        id: vendorId, filename: file.name,
        content_type: file.type || "application/octet-stream", byte_size: file.size
      })
      if (!presign.ok) throw new Error(presign.body.error || "That file couldn't be prepared for upload.")

      const put = await fetch(presign.body.upload_url, {
        method: "PUT",
        headers: { "Content-Type": presign.body.content_type },
        body: file
      })
      if (!put.ok) throw new Error("The upload didn't finish — try again.")

      const registered = await this._postJson(panel.dataset.uploadUrl, {
        id: vendorId, doc_type: input.dataset.docType,
        cache_name: presign.body.cache_name, filename: file.name
      })
      const data = registered.body || {}
      if (!registered.ok) throw new Error(data.error || "That document wasn't accepted.")
      // AI refused it — say why, and leave the row as it was.
      if (!data.ok) throw new Error((data.issues || []).join(" ") || "That document wasn't accepted.")

      await this._loadDocuments(vendorId)
    } catch (error) {
      if (text) text.textContent = original
      this._panelError(panel, error.message)
    }
  }

  async _postJson(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify(payload)
    })
    let body = {}
    try { body = await response.json() } catch (_error) { body = {} }
    return { ok: response.ok, body }
  }

  _panelError(panel, message) {
    let note = panel.querySelector("[data-doc-request-error]")
    if (!note) {
      note = document.createElement("p")
      note.setAttribute("data-doc-request-error", "")
      note.className = "text-[13px] text-red-600 mt-2"
      panel.appendChild(note)
    }
    note.textContent = message
  }

  closeViewModal() {
    this.viewModalTarget.classList.add("hidden")
  }

  editFromView() {
    if (!this._viewDataset) return
    this.closeViewModal()
    this._fillEditForm(this._viewDataset)
    this.editModalTarget.classList.remove("hidden")
  }

  // ── Edit Modal ────────────────────────────────────────────────────

  openEditModal(event) {
    this._fillEditForm(event.currentTarget.dataset)
    this.editModalTarget.classList.remove("hidden")
  }

  _fillEditForm(ds) {
    this.editIdTarget.value = ds.vendorId
    this.editNameTarget.value = ds.vendorName || ""
    this.editCreatedAtTarget.value = ds.vendorCreatedAt || ""
    this.editAddressTarget.value = ds.vendorAddress || ""
    this.editCountryTarget.value = ds.vendorCountryCode || ""
    this._loadEditSubregions(ds.vendorCountryCode, ds.vendorSubCountryCode)
    this.editCityTarget.value = ds.vendorCity || ""
    this.editZipcodeTarget.value = ds.vendorZipcode || ""
    this.editContactNameTarget.value = ds.vendorContactName || ""
    this.editContactEmailTarget.value = ds.vendorContactEmail || ""
    this.editContactPhoneTarget.value = ds.vendorContactPhone || ""
    this._loadContactUsers(ds.vendorId)
  }

  // Fill the Primary Contact user picker with the vendor company's users.
  // Picking one just prefills the fields — they stay editable, and manual
  // entry works by leaving the picker on "Enter manually below…".
  async _loadContactUsers(vendorId) {
    if (!this.hasEditContactUserSelectTarget) return
    const select = this.editContactUserSelectTarget
    select.innerHTML = '<option value="">Enter manually below…</option>'
    this._contactUsers = []

    try {
      const url = `${this.vendorUsersUrlValue}?id=${encodeURIComponent(vendorId)}`
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      this._contactUsers = data.users || []
      this._contactUsers.forEach((u, i) => {
        const opt = document.createElement("option")
        opt.value = String(i)
        opt.textContent = u.email && u.name !== u.email ? `${u.name} (${u.email})` : u.name
        select.appendChild(opt)
      })
    } catch (e) {
      // Picker stays manual-only if the lookup fails.
    }
  }

  contactUserPicked() {
    const idx = this.editContactUserSelectTarget.value
    if (idx === "") return
    const user = (this._contactUsers || [])[Number(idx)]
    if (!user) return
    this.editContactNameTarget.value = user.name || ""
    this.editContactEmailTarget.value = user.email || ""
    this.editContactPhoneTarget.value = user.phone || ""
  }

  closeEditModal() {
    this.editModalTarget.classList.add("hidden")
  }

  editCountryChanged() {
    this._loadEditSubregions(this.editCountryTarget.value)
  }

  async submitUpdateVendor() {
    const name = this.editNameTarget.value.trim()
    if (!name) {
      this.editNameTarget.focus()
      return
    }

    const btn = this.editSubmitBtnTarget
    btn.disabled = true
    btn.textContent = "Updating..."

    const body = {
      id: this.editIdTarget.value,
      vendor: {
        name: name,
        address: this.editAddressTarget.value.trim(),
        country_code: this.editCountryTarget.value.trim(),
        sub_country_code: this.editStateTarget.value.trim(),
        city: this.editCityTarget.value.trim(),
        zipcode: this.editZipcodeTarget.value.trim(),
        primary_contact_name: this.editContactNameTarget.value.trim(),
        primary_contact_email: this.editContactEmailTarget.value.trim(),
        primary_contact_phone: this.editContactPhoneTarget.value.trim()
      }
    }

    try {
      const resp = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify(body)
      })

      if (resp.ok) {
        this.closeEditModal()
        this._reloadFrame()
        this._showToast("Vendor updated successfully")
        track("Vendor Updated", { vendor_id: body.id })
      } else {
        const data = await resp.json()
        this._showToast(data.errors ? data.errors.join(", ") : "Error updating vendor", "error")
      }
    } catch (e) {
      this._showToast("Error updating vendor", "error")
    } finally {
      btn.disabled = false
      btn.textContent = "Update"
    }
  }

  // ── Backdrop close ────────────────────────────────────────────────

  // ── Bulk document chase ────────────────────────────────────────────
  // One click → every sub with stale/missing required docs gets the same
  // tokened upload email the per-vendor chase sends.
  openBulkRequestModal(event) {
    this._bulkRequestUrl = event.currentTarget.dataset.bulkRequestUrl
    this.bulkRequestModalTarget.classList.remove("hidden")
  }

  closeBulkRequestModal() {
    this.bulkRequestModalTarget.classList.add("hidden")
  }

  stopPropagation(event) { event.stopPropagation() }

  async confirmBulkRequest() {
    if (!this._bulkRequestUrl) return
    this.bulkRequestConfirmTarget.disabled = true
    try {
      const resp = await fetch(this._bulkRequestUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        const bits = [`Requested from ${data.requested} sub${data.requested === 1 ? "" : "s"}`]
        if (data.current) bits.push(`${data.current} already current`)
        if (data.no_email) bits.push(`${data.no_email} missing an email`)
        this._showToast(bits.join(" · "))
      } else {
        this._showToast(data.error || "Couldn't send the requests", "error")
      }
    } catch {
      this._showToast("Couldn't send the requests", "error")
    } finally {
      this.bulkRequestConfirmTarget.disabled = false
      this.closeBulkRequestModal()
    }
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      event.currentTarget.classList.add("hidden")
    }
  }

  // ── Export ─────────────────────────────────────────────────────────

  exportCsv() {
    // Close dropdown
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    this._showExportSpinner()

    const search = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    const params = new URLSearchParams()
    if (search) params.set("search", search)
    const url = `${this.exportUrlValue}?${params.toString()}`

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = blobUrl
        const today = new Date().toISOString().split("T")[0]
        a.download = `vendor-management-${today}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(blobUrl)
        this._showToast("Export downloaded successfully")
        track("Vendor List Exported", { format: "csv" })
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => this._hideExportSpinner())
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  // ── Internals ─────────────────────────────────────────────────────

  _loadCreateSubregions(countryCode) {
    this._loadSubregions(this.createStateTarget, countryCode)
  }

  _loadEditSubregions(countryCode, selectedCode) {
    this._loadSubregions(this.editStateTarget, countryCode, selectedCode)
  }

  _loadSubregions(select, countryCode, selectedCode) {
    select.innerHTML = '<option value=""></option>'

    if (!countryCode) return

    fetch(`/locations/subregions?country_code=${countryCode}`, {
      headers: { "Accept": "application/json" }
    })
    .then(r => r.json())
    .then(subregions => {
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.code
        opt.textContent = sr.name
        if (selectedCode && sr.code === selectedCode) opt.selected = true
        select.appendChild(opt)
      })
    })
  }

  // ── Remove + trash ────────────────────────────────────────────────

  confirmRemoveVendor(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    this._pendingRemoval = { id: btn.dataset.vendorId, name: btn.dataset.vendorName }
    this.removeNameTarget.textContent = this._pendingRemoval.name || ""
    this.removeModalTarget.classList.remove("hidden")
  }

  closeRemoveModal() {
    this.removeModalTarget.classList.add("hidden")
    this._pendingRemoval = null
  }

  async confirmRemove() {
    if (!this._pendingRemoval) return
    const { id, name } = this._pendingRemoval
    const btn = this.removeConfirmTarget
    btn.disabled = true

    try {
      const resp = await fetch(this.removeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({ id })
      })
      if (!resp.ok) throw new Error("failed")

      track("Vendor Removed", { vendor_id: Number(id), user_role: this.labelValue })
      this.closeRemoveModal()
      this._reloadFrame()
      this._showToast(`${name} was removed`)
    } catch {
      this._showToast(`Couldn't remove ${name}`, "error")
    } finally {
      btn.disabled = false
    }
  }

  async openRemovedVendors() {
    this.removedModalTarget.classList.remove("hidden")
    this.removedEmptyTarget.classList.add("hidden")
    this.removedLoadingTarget.classList.remove("hidden")
    this._removedBody().innerHTML = ""

    try {
      const resp = await fetch(this.removedListUrlValue, { headers: { Accept: "application/json" } })
      if (!resp.ok) throw new Error("failed")
      const data = await resp.json()
      this._removedVendors = data.vendors || []
      this._renderRemovedVendors(this._removedVendors)
    } catch {
      this._showToast("Could not load the trash", "error")
    } finally {
      this.removedLoadingTarget.classList.add("hidden")
    }
  }

  closeRemovedVendors() {
    this.removedModalTarget.classList.add("hidden")
  }

  searchRemovedVendors(event) {
    const terms = event.currentTarget.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
    this._renderRemovedVendors((this._removedVendors || []).filter(vendor => {
      const haystack = [vendor.name, vendor.contact, vendor.removed_by, vendor.removed_on]
        .filter(Boolean).join(" ").toLowerCase()
      return terms.every(term => haystack.includes(term))
    }))
  }

  _removedBody() {
    return this.removedModalTarget.querySelector(".js-removed-vendors")
  }

  _renderRemovedVendors(vendors) {
    const body = this._removedBody()
    body.innerHTML = ""
    this.removedEmptyTarget.classList.toggle("hidden", vendors.length > 0)

    vendors.forEach(vendor => {
      const tr = document.createElement("tr")
      tr.className = "bg-white"
      tr.innerHTML = `
        <td class="px-2 py-1.5">
          <div class="text-gray-900">${this._escapeHtml(vendor.name)}</div>
          ${vendor.contact ? `<div class="text-gray-400">${this._escapeHtml(vendor.contact)}</div>` : ""}
        </td>
        <td class="px-2 py-1.5 text-center text-gray-600">${this._escapeHtml(vendor.removed_on || "—")}</td>
        <td class="px-2 py-1.5 text-center text-gray-600">${this._escapeHtml(vendor.removed_by || "—")}</td>
        <td class="px-2 py-1.5 text-right"></td>
      `
      const button = document.createElement("button")
      button.type = "button"
      button.className = "px-3 py-1 text-[13px] text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer"
      button.textContent = "Restore"
      button.addEventListener("click", () => this._restoreVendor(vendor, tr, button))
      tr.lastElementChild.appendChild(button)

      // "Delete forever" (Larry 2026-08-12: "otherwise this just keeps
      // getting bigger and bigger"). Two-step arm instead of a browser
      // confirm — first click turns it red and asks, second commits.
      const purge = document.createElement("button")
      purge.type = "button"
      purge.className = "ml-2 px-3 py-1 text-[13px] text-gray-500 border border-gray-300 hover:text-red-600 hover:border-red-300 rounded-lg cursor-pointer"
      purge.textContent = "Delete forever"
      purge.addEventListener("click", () => this._purgeVendor(vendor, tr, purge))
      tr.lastElementChild.appendChild(purge)
      body.appendChild(tr)
    })
  }

  async _purgeVendor(vendor, row, button) {
    if (button.dataset.armed !== "true") {
      button.dataset.armed = "true"
      button.textContent = "Forever?"
      button.classList.add("text-red-600", "border-red-300", "bg-red-50")
      setTimeout(() => {
        button.dataset.armed = "false"
        button.textContent = "Delete forever"
        button.classList.remove("text-red-600", "border-red-300", "bg-red-50")
      }, 3000)
      return
    }
    button.disabled = true

    try {
      const resp = await fetch(this.purgeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({ id: vendor.id })
      })
      if (!resp.ok) throw new Error("failed")

      row.remove()
      this._removedVendors = (this._removedVendors || []).filter(candidate => candidate.id !== vendor.id)
      this.removedEmptyTarget.classList.toggle("hidden", this._removedBody().children.length > 0)
      this._showToast(`${vendor.name} deleted from the trash`)
    } catch {
      button.disabled = false
      button.textContent = "Delete forever"
      this._showToast(`Couldn't delete ${vendor.name}`, "error")
    }
  }

  async _restoreVendor(vendor, row, button) {
    button.disabled = true
    button.innerHTML = '<span class="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'

    try {
      const resp = await fetch(this.restoreUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({ id: vendor.id })
      })
      if (!resp.ok) throw new Error("failed")

      track("Vendor Restored", { vendor_id: Number(vendor.id), user_role: this.labelValue })
      row.remove()
      this._removedVendors = (this._removedVendors || []).filter(candidate => candidate.id !== vendor.id)
      this.removedEmptyTarget.classList.toggle("hidden", this._removedBody().children.length > 0)
      this._reloadFrame()
      this._showToast(`${vendor.name} is back on your list`)
    } catch {
      button.disabled = false
      button.textContent = "Restore"
      this._showToast(`Couldn't restore ${vendor.name}`, "error")
    }
  }

  _reloadFrame() {
    const frame = this.frameTarget
    const search = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""

    const params = new URLSearchParams()
    params.set("format", "html")
    params.set("page", "1")
    params.set("sort_by", this.currentSort)
    params.set("sort_order", this.currentOrder)
    if (search) params.set("search", search)

    frame.src = `${this.listVendorsUrlValue}?${params.toString()}`
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
